import { formatMoney } from "@/lib/shop/format";

export const BASE_CURRENCY = "CNY";

export const SITE_CURRENCIES = [
  "CNY",
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "KRW",
  "TWD",
  "HKD",
  "MOP",
  "SGD",
  "AUD",
  "CAD",
  "RUB",
  "BRL",
] as const;

export const PRICE_ROUNDINGS = ["99", "integer", "none"] as const;
export type PriceRounding = (typeof PRICE_ROUNDINGS)[number];

export type SiteCurrency = {
  code: string;

  rate: number;
  rounding: PriceRounding;

  locale: string;
};

export const BASE_SITE_CURRENCY: SiteCurrency = {
  code: BASE_CURRENCY,
  rate: 1,
  rounding: "none",
  locale: "zh-CN",
};

export function siteCurrencyOf(site: {
  currency: string;
  exchangeRate: number;
  priceRounding: string;
  locale: string;
}): SiteCurrency {
  return {
    code: site.currency || BASE_CURRENCY,
    rate: site.exchangeRate > 0 ? site.exchangeRate : 1,
    rounding: (PRICE_ROUNDINGS as readonly string[]).includes(site.priceRounding)
      ? (site.priceRounding as PriceRounding)
      : "99",
    locale: site.locale || "zh-CN",
  };
}

function fractionDigitsOf(code: string): number {
  return new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions()
    .maximumFractionDigits ?? 2;
}

function roundTo(value: number, digits: number): number {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

export function exchange(cny: number, currency: SiteCurrency): number {
  return roundTo(cny * currency.rate, fractionDigitsOf(currency.code));
}

export function convertPrice(cny: number, currency: SiteCurrency): number {
  if (currency.code === BASE_CURRENCY || cny <= 0) {
    return cny;
  }

  if (currency.rounding === "none") {
    return exchange(cny, currency);
  }

  const whole = Math.ceil(roundTo(cny * currency.rate, 2));
  return currency.rounding === "99" && fractionDigitsOf(currency.code) > 0
    ? roundTo(whole - 0.01, 2)
    : whole;
}

export function formatCurrency(amount: number, code: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: code }).format(amount);
}

export function currencyParts(
  amount: number,
  code: string,
  locale: string,
): { symbol: string; number: string; symbolAfter: boolean } {
  const parts = new Intl.NumberFormat(locale, { style: "currency", currency: code }).formatToParts(amount);
  const symbolIndex = parts.findIndex((part) => part.type === "currency");
  const numberIndex = parts.findIndex((part) => part.type === "integer");

  return {
    symbol: parts[symbolIndex]?.value ?? "",
    number: parts
      .filter((part) => part.type !== "currency" && part.type !== "literal")
      .map((part) => part.value)
      .join(""),
    symbolAfter: symbolIndex > numberIndex,
  };
}

export type SiteMoney = {

  converted: boolean;

  priceValue: (cny: number) => number;

  format: (amount: number) => string;
  parts: (amount: number) => ReturnType<typeof currencyParts>;

  price: (cny: number) => string;

  base: (cny: number) => string;

  approx: (cny: number) => string;
};

export function createSiteMoney(currency: SiteCurrency): SiteMoney {
  const converted = currency.code !== BASE_CURRENCY;
  const format = (amount: number) =>
    converted ? formatCurrency(amount, currency.code, currency.locale) : formatMoney(amount);

  return {
    converted,
    priceValue: (cny) => convertPrice(cny, currency),
    format,
    parts: (amount) =>
      converted
        ? currencyParts(amount, currency.code, currency.locale)
        : { symbol: "¥", number: amount.toFixed(2), symbolAfter: false },
    price: (cny) => format(convertPrice(cny, currency)),
    base: (cny) =>
      converted ? formatCurrency(cny, BASE_CURRENCY, currency.locale) : formatMoney(cny),
    approx: (cny) => (converted ? format(exchange(cny, currency)) : ""),
  };
}
